import type {
  IncidentsBucket,
  IncidentsTimesSectionData,
  OutcomesEvolutionBucket,
  OutcomesSectionData,
  TaskSummarySectionData,
  TemplateHistoryMetric,
  UsersSectionData,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';

const MS_PER_MINUTE = 60_000;

const percentage = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0);

/**
 * Done orders: completed by the assignees or closed by a supervisor (ADR 0002). Every order
 * compliance rate uses it; assignee metrics (users section) never include the closures.
 */
const doneOrders = (completed: number, supervisorClosed = 0) => completed + supervisorClosed;

/** Values of the order KPI cards; `null` when there is nothing to measure. */
export const getOrderKpiValues = ({
  orders,
  completed,
  supervisorClosed = 0,
  resultCohort,
  completedLate,
  delayed,
  durationToComplete,
}: TaskSummarySectionData) => ({
  orders,
  completed,
  supervisorClosed,
  completionRate: resultCohort > 0 ? percentage(doneOrders(completed, supervisorClosed), resultCohort) : null,
  late: completedLate,
  delay: delayed,
  averageDurationMinutes:
    durationToComplete.count > 0
      ? Math.round(durationToComplete.totalMs / durationToComplete.count / MS_PER_MINUTE)
      : null,
});

export type PeriodResultKey =
  | 'completed_on_time'
  | 'completed_late'
  | 'supervisor_closed'
  | 'partial'
  | 'no_activity'
  | 'canceled';

/**
 * Period outcome slices; percentages are over every order of the period, canceled included.
 * The supervisor-closed slice only appears when the period has closures, so organizations
 * without the feature keep their five slices.
 */
export const getPeriodResult = ({
  onTime,
  late,
  supervisorClosed = 0,
  partial,
  zero,
  canceled,
}: OutcomesSectionData['result']) => {
  const total = onTime + late + supervisorClosed + partial + zero + canceled;
  const slices: Array<[PeriodResultKey, number]> = [
    ['completed_on_time', onTime],
    ['completed_late', late],
    ...(supervisorClosed > 0 ? [['supervisor_closed', supervisorClosed] as [PeriodResultKey, number]] : []),
    ['partial', partial],
    ['no_activity', zero],
    ['canceled', canceled],
  ];
  return slices.map(([key, value]) => ({ key, value, percentage: percentage(value, total) }));
};

/** Compliance evolution points, each bucket rated over its result cohort; `null` without orders. */
export const getEvolutionPoints = (evolution: OutcomesEvolutionBucket[]) =>
  evolution.map(({ bucketStart, completed, supervisorClosed, resultCohort }) => ({
    date: bucketStart,
    completed: doneOrders(completed, supervisorClosed),
    total: resultCohort,
    completionRate: resultCohort > 0 ? percentage(doneOrders(completed, supervisorClosed), resultCohort) : null,
  }));

const toMinutes = (milliseconds: number) => Math.round(milliseconds / MS_PER_MINUTE);

/** `52 min` or `8 h 06 min`. */
export const formatCompactDuration = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  const remaining = String(minutes % 60).padStart(2, '0');
  return `${Math.floor(minutes / 60)} h ${remaining} min`;
};

/** Incident series keyed like the incident chart categories. */
export const getIncidentPoints = (series: IncidentsBucket[]) =>
  series.map(({ bucketStart, normal, delayed, late, zero }) => ({
    date: bucketStart,
    normal,
    delay: delayed,
    late,
    unfulfilled: zero,
  }));

/** Delay indicators; `null` when there is nothing to rate or average. */
export const getDelayMetrics = ({ orders, resultCohort, totalMs, maxMs }: IncidentsTimesSectionData['delay']) => ({
  orders,
  percent: resultCohort > 0 ? percentage(orders, resultCohort) : null,
  averageMinutes: orders > 0 ? toMinutes(totalMs / orders) : null,
  totalMinutes: toMinutes(totalMs),
  maxMinutes: toMinutes(maxMs),
});

/** Late-completion indicators; the average only covers late orders with a known completion. */
export const getExtraTimeMetrics = ({
  lateOrders,
  completed,
  measured,
  totalMs,
  maxMs,
}: IncidentsTimesSectionData['extraTime']) => ({
  lateOrders,
  completed,
  lateRate: completed > 0 ? percentage(lateOrders, completed) : null,
  averageMinutes: measured > 0 ? toMinutes(totalMs / measured) : null,
  totalMinutes: toMinutes(totalMs),
  maxMinutes: toMinutes(maxMs),
});

export type TemplateRankingMetric = ReturnType<typeof getTemplateMetrics>[number];

/** Template metrics for the rankings; compliance is rated over the result cohort. */
export const getTemplateMetrics = (templates: TemplateHistoryMetric[]) =>
  templates.map(({ templateId, templateName, orders, completed, supervisorClosed, resultCohort }) => ({
    templateId,
    templateName,
    orders,
    completed: doneOrders(completed, supervisorClosed),
    resultCohort,
    completionRate: resultCohort > 0 ? percentage(doneOrders(completed, supervisorClosed), resultCohort) : null,
  }));

export type RatedTemplateMetric = TemplateRankingMetric & { completionRate: number };

/** Only templates with a result cohort have a compliance to rank. */
export const hasCompletionRate = (template: TemplateRankingMetric): template is RatedTemplateMetric =>
  template.completionRate !== null;

/** Values of the user KPI cards. */
export const getUserKpiValuesFromSection = ({
  assigned,
  withActivity,
  fullCompliance,
  partialCompliance,
  noActivity,
  assignmentPairs,
}: UsersSectionData) => ({
  assigned,
  active: withActivity,
  complete: fullCompliance,
  partial: partialCompliance,
  inactive: noActivity,
  averageOrders: assigned > 0 ? assignmentPairs / assigned : 0,
});
